import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * メッセージ下書き API 操作クラス
 */
export default class DraftMessages {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET DraftMessages
   */
  public async get(userStore: LoginUser, id: number): Promise<DraftMessagesGetModel> {
    // 施設ID、イベントIDの場合のURL作成
    const formatParams: any = {
      facilityId: userStore.facility.id,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DraftMessage, formatParams);
    const response = await this.commonService.executeGet<DraftMessagesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET DraftMessages
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex: number; endIndex: number }): Promise<Array<DraftMessagesGetModel>> {
    // 施設IDのみの場合のURL作成
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DraftMessages, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<DraftMessagesGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GETの返却用Model作成
   *
   * @param body レスポンスボディ
   */
  private createGetResponse(body: any): Array<DraftMessagesGetModel> {
    // 配列で返したいので、配列でなければ格納しなおす
    let response: Array<DraftMessagesGetModel> = [];

    if (Array.isArray(body)) {
      response = body;
    } else {
      response.push(body);
    }

    return response;
  }

  /**
   * ### POST DraftMessages
   */
  public async post(userStore: LoginUser, requestBody: DraftMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DraftMessages, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH DraftMessages
   */
  public async patch(userStore: LoginUser, id: number, requestBody: DraftMessagesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DraftMessage, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE DraftMessages
   */
  public async delete(userStore: LoginUser, id: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.DraftMessage, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface DraftMessagesGetModel {
  /** 施設ID(パーティションキー) */
  facilityId: number;
  /** 識別ID(ソートキー) */
  id: number;
  /** 宛先種別 */
  type?: DraftMessageSendTargetType;
  /**
   * 宛先
   *
   * - <家族の場合>
   *   - residentsSortKeyを配列で持つ
   * - <施設関係者の場合>
   *   - otherUserGroupsSortKeyを配列で持つ
   * - <宛先グループの場合>
   *   - sendMessageGroupsSortKeyを配列で持つ
   */
  targets?: string[];
  /** タイトル */
  title?: string;
  /** 本文 */
  content?: string;
  /** メッセージの返信が欲しいことを強調させるか */
  addOptionText?: boolean;
  /**  見ましたをつけるか*/
  addReadButton?: boolean;
  /** 返信をつけるか */
  addReplyButton?: boolean;
  /** 添付ファイル */
  files?: Array<DraftMessageFile>;
  /** 記載者ID */
  writerId: string;
  /** 記載者 */
  writtenBy: string;
  /** 更新日時 */
  updated: number;
  /** 作成日時 */
  created: number;
}

export enum DraftMessageSendTargetType {
  /** 家族全員 */
  ALL_FAMILY = 0,
  /** 家族個別 */
  SELECTED_FAMILY = 1,
  /** 施設関係者個別 */
  SELECTED_OTHER_USER_GROUP = 2,
  /** 宛先グループ */
  SEND_MESSGE_GROUP = 3,
  /** 家族全員 */
  ALL_WAITER = 4,
  /** 家族個別 */
  SELECTED_WAITER = 5
}

export interface DraftMessageFile {
  /** ファイル名 */
  name: string;
  /** S3URL */
  url: string;
}

export interface DraftMessagesPostModel {
  /** 宛先種別 */
  type?: DraftMessageSendTargetType;
  /**
   * 宛先
   *
   * - <家族の場合>
   *   - residentsSortKeyを配列で持つ
   * - <施設関係者の場合>
   *   - otherUserGroupsSortKeyを配列で持つ
   * - <宛先グループの場合>
   *   - sendMessageGroupsSortKeyを配列で持つ
   */
  targets?: string[];
  /** タイトル */
  title?: string;
  /** 本文 */
  content?: string;
  /** メッセージの返信が欲しいことを強調させるか */
  addOptionText?: boolean;
  /**  見ましたをつけるか*/
  addReadButton?: boolean;
  /** 返信をつけるか */
  addReplyButton?: boolean;
  /** 添付ファイル */
  files?: Array<DraftMessageFile>;
}

export interface DraftMessagesPatchModel {
  /** 宛先種別 */
  type?: DraftMessageSendTargetType;
  /**
   * 宛先
   *
   * - <家族の場合>
   *   - residentsSortKeyを配列で持つ
   * - <施設関係者の場合>
   *   - otherUserGroupsSortKeyを配列で持つ
   * - <宛先グループの場合>
   *   - sendMessageGroupsSortKeyを配列で持つ
   */
  targets?: string[];
  /** タイトル */
  title?: string;
  /** 本文 */
  content?: string;
  /** メッセージの返信が欲しいことを強調させるか */
  addOptionText?: boolean;
  /**  見ましたをつけるか*/
  addReadButton?: boolean;
  /** 返信をつけるか */
  addReplyButton?: boolean;
  /** 添付ファイル */
  files?: Array<DraftMessageFile>;
  /** 旧添付ファイル */
  oldFiles?: Array<DraftMessageFile>;
  /** 更新日時 */
  updated?: number;
}
